import type { ApiFailure, Paginated } from '@sirajoptic/shared';
import { fermerSession, jetonCourant } from '@/lib/session';

/**
 * Client HTTP unique. En développement, Vite mandate `/api` vers wrangler ;
 * en production l'application et le Worker partagent l'origine, sauf si
 * `VITE_API_URL` est fourni.
 *
 * Deux formes d'authentification cohabitent, et aucune n'a à être choisie ici :
 *
 * - **Cloudflare Access** dépose un cookie sur le domaine ; `credentials:
 *   'include'` suffit à le transmettre ;
 * - **la session applicative** voyage en `Authorization: Bearer`.
 *
 * Le serveur tranche selon sa configuration ; le client présente simplement ce
 * qu'il a.
 */
const BASE = import.meta.env.VITE_API_URL ?? '';
const PREFIXE = `${BASE}/api/v1`;

function enTetesCommuns(avecCorps: boolean): Record<string, string> {
  const jeton = jetonCourant();
  return {
    ...(avecCorps ? { 'Content-Type': 'application/json' } : {}),
    ...(jeton ? { Authorization: `Bearer ${jeton}` } : {}),
  };
}

/** Erreur portant le code métier de l'API, pour cibler un champ du formulaire. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  /** Messages par champ, renvoyés par la validation Zod du serveur. */
  readonly details: Record<string, string> | undefined;

  constructor(status: number, code: string, message: string, details?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Params = Record<string, string | number | boolean | null | undefined>;

function versQuery(params?: Params): string {
  if (!params) return '';
  const q = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur === null || valeur === undefined || valeur === '' || valeur === false) continue;
    q.set(cle, String(valeur));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

function estEchec(charge: unknown): charge is ApiFailure {
  return (
    typeof charge === 'object' &&
    charge !== null &&
    'error' in charge &&
    typeof (charge as ApiFailure).error?.message === 'string'
  );
}

/**
 * Exécute la requête et rend la charge utile brute, ou lève une `ApiError`.
 * Les listes paginées ont besoin de l'enveloppe entière (`data` **et** `meta`),
 * d'où la séparation : le traitement des erreurs, lui, ne doit exister qu'une
 * fois — une seconde copie finirait par oublier un cas, comme la session
 * expirée.
 */
async function envoyer(
  chemin: string,
  options: RequestInit & { params?: Params } = {},
): Promise<unknown> {
  const { params, ...init } = options;

  let reponse: Response;
  try {
    reponse = await fetch(`${PREFIXE}${chemin}${versQuery(params)}`, {
      ...init,
      headers: {
        ...enTetesCommuns(Boolean(init.body)),
        ...init.headers,
      },
      // Nécessaire au cookie de Cloudflare Access ; sans effet en mode session.
      credentials: 'include',
    });
  } catch {
    /* `fetch` ne rejette que si la requête n'a pas abouti du tout : serveur
     * arrêté, connexion coupée, DNS muet. Le distinguer d'une erreur renvoyée
     * par l'API n'est pas cosmétique — « réessayez dans un instant » envoie
     * recommencer indéfiniment là où il faut d'abord rallumer quelque chose. */
    throw new ApiError(
      0,
      'RESEAU',
      'Le serveur est injoignable. Vérifiez votre connexion, ou que le serveur est bien démarré.',
    );
  }

  const charge: unknown = await reponse.json().catch(() => null);

  if (reponse.status === 401 || reponse.status === 403) {
    /* Le jeton présenté ne vaut plus rien : on l'oublie tout de suite. Les
     * écrans montés l'apprennent par l'abonnement à la session et renvoient
     * vers la page de connexion — le client HTTP n'a pas à connaître le
     * routeur. */
    fermerSession();

    if (estEchec(charge)) {
      throw new ApiError(reponse.status, charge.error.code, charge.error.message);
    }
    throw new ApiError(
      reponse.status,
      'NON_AUTORISE',
      'Session expirée. Reconnectez-vous pour continuer.',
    );
  }

  if (!reponse.ok) {
    if (estEchec(charge)) {
      const details = charge.error.details;
      throw new ApiError(
        reponse.status,
        charge.error.code,
        charge.error.message,
        details && typeof details === 'object' ? (details as Record<string, string>) : undefined,
      );
    }
    throw new ApiError(reponse.status, 'INTERNE', `Erreur ${reponse.status}. Réessayez.`);
  }

  /* Une réponse 2xx qui n'est pas l'enveloppe attendue signifie que la requête
   * n'a pas atteint l'API. Le cas concret : Pages sert `index.html` avec un
   * code 200 pour toute adresse qu'il ne connaît pas, si bien qu'une route
   * Worker absente ou mal configurée ressemble à un succès. Sans ce contrôle,
   * la suite lirait `.data` sur `null` et l'écran afficherait une erreur de
   * programmation au lieu de la cause réelle. */
  if (charge === null || typeof charge !== 'object' || !('data' in charge)) {
    throw new ApiError(
      reponse.status,
      'REPONSE_INVALIDE',
      "Réponse inattendue du serveur : l'API n'est pas joignable à cette adresse.",
    );
  }

  return charge;
}

export const api = {
  get: async <T>(chemin: string, params?: Params) =>
    ((await envoyer(chemin, { params })) as { data: T }).data,

  /** Variante conservant `meta` pour les listes paginées. */
  liste: async <T>(chemin: string, params?: Params) =>
    (await envoyer(chemin, { params })) as Paginated<T>,

  post: async <T>(chemin: string, corps?: unknown) =>
    (
      (await envoyer(chemin, {
        method: 'POST',
        body: corps === undefined ? undefined : JSON.stringify(corps),
      })) as { data: T }
    ).data,

  patch: async <T>(chemin: string, corps: unknown) =>
    ((await envoyer(chemin, { method: 'PATCH', body: JSON.stringify(corps) })) as { data: T }).data,

  delete: async <T>(chemin: string) =>
    ((await envoyer(chemin, { method: 'DELETE' })) as { data: T }).data,
};
